import { HttpErrorResponse } from '@angular/common/http';

import { SESSION_EXPIRED_MESSAGE } from '@shared/utils/async-state.util';
import { userCountTextOf } from '@shared/utils/user-count-text.util';

import type { ProblemDetailsDto } from './problem-details.types';

const RFC7807_FIELDS = new Set(['type', 'title', 'status', 'detail', 'instance']);

const GENERIC_ERROR_MESSAGE = 'Ocorreu um erro. Tente novamente.';

const OFFLINE_MESSAGE = 'Sem conexão com o servidor. Verifique sua internet.';

const MESSAGE_BY_STATUS: Readonly<Record<number, string>> = {
  401: SESSION_EXPIRED_MESSAGE,
  403: 'Você não tem permissão para executar esta ação.',
  404: 'Recurso não encontrado.',
  429: 'Muitas tentativas. Aguarde alguns minutos antes de tentar de novo.',
};

const NO_CONNECTION_STATUS = 0;

const SERVER_ERROR_MESSAGE = 'Erro interno do servidor. Tente novamente mais tarde.';

const SERVER_ERROR_STATUS = 500;

const MISSING_LOOKUPS_EXTENSION = 'missingLookups';

const AFFECTED_USERS_TITLE = 'Forbidden.affectedUsers';

const ABOVE_ACTOR_COUNT_EXTENSION = 'aboveActorCount';

const VALIDATION_ERROR_TITLE_PREFIX = 'ValidationError.';

export function isHttpErrorResponse(value: unknown): value is HttpErrorResponse {
  return value instanceof HttpErrorResponse;
}

export function toProblemDetails(payload: unknown): ProblemDetailsDto | null {
  if (!isProblemDetailsShape(payload)) {
    return null;
  }

  const raw = payload;

  return {
    type: stringOrUndefined(raw['type']),
    title: stringOrUndefined(raw['title']),
    status: numberOrUndefined(raw['status']),
    detail: stringOrUndefined(raw['detail']),
    instance: stringOrUndefined(raw['instance']),
    extensions: raw['extensions'] && typeof raw['extensions'] === 'object' ? (raw['extensions'] as Record<string, unknown>) : extractCustomFields(raw),
  };
}

export function messageFromProblem(problem: ProblemDetailsDto): string {
  return (affectedUsersMessageOf(problem) ?? problem.detail) || problem.title || GENERIC_ERROR_MESSAGE;
}

export function messageOfFailure(failure: unknown): string {
  if (isHttpErrorResponse(failure)) {
    return messageFromHttpError(failure);
  }

  const problem = toProblemDetails(failure);

  return problem ? messageFromProblem(problem) : GENERIC_ERROR_MESSAGE;
}

export function problemOfFailure(failure: unknown): ProblemDetailsDto | null {
  return toProblemDetails(isHttpErrorResponse(failure) ? failure.error : failure);
}

export function missingLookupsOf(failure: unknown): readonly number[] {
  const missingLookups = problemOfFailure(failure)?.extensions?.[MISSING_LOOKUPS_EXTENSION];

  return Array.isArray(missingLookups) ? missingLookups.filter((id): id is number => typeof id === 'number') : [];
}

export function numberExtensionOf(failure: unknown, extension: string): number | null {
  const value = problemOfFailure(failure)?.extensions?.[extension];

  return typeof value === 'number' ? value : null;
}

export function rejectedFieldOf<TField extends string>(failure: unknown, fields: readonly TField[]): TField | null {
  const title = problemOfFailure(failure)?.title;

  return fields.find(field => title === `${VALIDATION_ERROR_TITLE_PREFIX}${field}`) ?? null;
}

export function messageFromHttpError(error: HttpErrorResponse): string {
  if (error.status === NO_CONNECTION_STATUS) {
    return OFFLINE_MESSAGE;
  }

  const body: unknown = error.error;

  if (typeof body === 'string' && body.trim()) {
    return body;
  }

  const problem = toProblemDetails(body);

  if (problem) {
    return messageFromProblem(problem);
  }

  if (error.status >= SERVER_ERROR_STATUS) {
    return SERVER_ERROR_MESSAGE;
  }

  return MESSAGE_BY_STATUS[error.status] || error.message || GENERIC_ERROR_MESSAGE;
}

function affectedUsersMessageOf(problem: ProblemDetailsDto): string | null {
  const count = problem.extensions?.[ABOVE_ACTOR_COUNT_EXTENSION];

  if (problem.title !== AFFECTED_USERS_TITLE || typeof count !== 'number') {
    return null;
  }

  const verb = count === 1 ? 'não está' : 'não estão';

  return `Não foi possível salvar: ${userCountTextOf(count)} com este papel ${verb} abaixo de você, antes ou depois da mudança.`;
}

function isProblemDetailsShape(value: unknown): value is Record<string, unknown> {
  if (!value || typeof value !== 'object') {
    return false;
  }

  if (value instanceof Event) {
    return false;
  }

  return ['title', 'detail', 'status', 'type', 'instance'].some(field => field in value);
}

function extractCustomFields(payload: Record<string, unknown>): Record<string, unknown> {
  const customFields: Record<string, unknown> = {};

  for (const [key, value] of Object.entries(payload)) {
    if (!RFC7807_FIELDS.has(key)) {
      customFields[key] = value;
    }
  }

  return customFields;
}

function numberOrUndefined(value: unknown): number | undefined {
  const candidate = typeof value === 'string' ? Number(value) : value;

  return typeof candidate === 'number' && Number.isFinite(candidate) ? candidate : undefined;
}

function stringOrUndefined(value: unknown): string | undefined {
  return typeof value === 'string' ? value : undefined;
}
